import { Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import AdvancedImageLoading from "../../../../components/TailoredGeneration/SharedPanels/AdvancedImageLoading/AdvancedImageLoading";
import AdvancedImageLoadingCompleted from "../../../../components/TailoredGeneration/SharedPanels/AdvancedImageLoadingCompleted/AdvancedImageLoadingCompleted";
import AdvancedImageLoadingError from "../../../../components/TailoredGeneration/SharedPanels/AdvancedImageLoadingError/AdvancedImageLoadingError";
import SelectTgUploadMethod from "../../../../components/TailoredGeneration/SharedPanels/SelectTgUploadMethod/SelectTgUploadMethod.tsx";
import TgAdvancedImageUploadSetup from "../../../../components/TailoredGeneration/SharedPanels/TgAdvancedImageUploadSetup/TgAdvancedImageUploadSetup.tsx";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import RouterConstants from "../../../../constants/RouterConstants.ts";
import useSecureNavigate from "../../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../../hooks/useStores";
import EditorLayout from "../../../../layout/EditorLayout/EditorLayout";
import { BulkUploadStatus, TgDataset, defaultDataset } from "../../Datasets/store/tg-dataset-store.tsx";
import CreateTgModelDataset from "../../Projects/Viewer/Models/Create/Dataset/CreateTgModelDataset.tsx";
import BestPractisesButton from "../../../../components/common/BestPractisesButton/BestPractisesButton.tsx";
import CreateTgModelPopup from "../Popups/CreateModel/CreateTgModelPopup";
import { CreateTgModelPanels, TrainingMode, defaultModel } from "../store/tg-model-store.tsx";
import styles from "./CreateTgModel.module.scss";
import SelectTgDatasetStep from "./Dataset/SelectTgDatasetStep";
import TgExpertParametersSetup from "./TgExpertParametersSetup/TgExpertParametersSetup.tsx";
import TrainingModeSelection from "./TrainingModeSelection/TrainingModeSelection";
import TgTrainingVersionStep from "./TrainingVersion/TgTrainingVersionStep.tsx";

const CreateTgModel = () => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create" });
	const { tgStore } = useAppStore();
	const { tgModelStore, tgDatasetStore } = tgStore;
	const [currentPanel, setCurrentPanel] = useState<CreateTgModelPanels>(CreateTgModelPanels.TRAINING_MODE_PANEL);
	const [initialStatus, setInitialStatus] = useState<BulkUploadStatus | null>(null);
	const [lastStatus, setLastStatus] = useState<BulkUploadStatus | null>(null);
	const [openCreateModelPopup, setOpenCreateModelPopup] = useState(false);
	const stillUploadingImages = tgDatasetStore.datasetForm.images.filter((img) => img.uploading);
	const uploadedImages = tgDatasetStore.datasetForm.images.filter((img) => !img.uploading && !img.error);
	const loadingDatasetSave =
		tgDatasetStore.loadingUpdateDataset ||
		tgDatasetStore.loadingDuplicateDataset ||
		tgDatasetStore.loadingUploadImages ||
		tgDatasetStore.loadingRegenerate;
	const navigate = useSecureNavigate();
	const { projectId } = useParams();

	useEffect(() => {
		return () => {
			if (
				!tgModelStore.isDatasetFromExisting &&
				tgDatasetStore.datasetForm.status === "Draft" &&
				tgDatasetStore.datasetForm.id
			) {
				tgDatasetStore.deleteDataset(tgDatasetStore.datasetForm.id);
			}
			tgModelStore.isDatasetFromExisting = false;
			tgDatasetStore.datasetForm = defaultDataset;
			tgModelStore.modelForm = defaultModel;
			tgDatasetStore.isFormDuplicateTriggered = false;
			window.history.replaceState({}, "");
			tgDatasetStore.csvUploadErrors = [];
			tgDatasetStore.csvUploadWarnings = [];
		};
	}, []);

	const primaryButtonOnClickHandler = () => {
		if (tgModelStore.modelForm.training_mode === TrainingMode.EXPERT) {
			setCurrentPanel(CreateTgModelPanels.PARAMETERS_SETUP_PANEL);
		} else {
			setOpenCreateModelPopup(true);
		}
	};

	const getPrimaryButton = () => {
		return (
			currentPanel === CreateTgModelPanels.CREATE_MODEL_DATASET_PANEL && (
				<>
					{stillUploadingImages.length ? (
						<>
							<Typography className={styles.uploadLoader}>{t("uploadLoader")}</Typography>
							<Typography className={styles.uploadLoaderCount}>
								{`${uploadedImages.length}/${tgDatasetStore.datasetForm.images.length}`}
							</Typography>
						</>
					) : (
						<BriaButton
							buttonType="primaryMedium"
							onClick={primaryButtonOnClickHandler}
							disabled={
								tgDatasetStore.loadingDuplicateDataset ||
								!uploadedImages.length ||
								(tgModelStore.modelForm.training_mode === TrainingMode.EXPERT &&
									!tgDatasetStore.datasetForm.name?.trim())
							}
						>
							{tgModelStore.modelForm.training_mode === TrainingMode.EXPERT
								? t("primaryButtonExpert")
								: t("primaryButton")}
						</BriaButton>
					)}
				</>
			)
		);
	};

	const getSecondaryButton = () => {
		return (
			currentPanel !== CreateTgModelPanels.TRAINING_MODE_PANEL &&
			currentPanel !== CreateTgModelPanels.CREATE_MODEL_DATASET_PANEL &&
			currentPanel !== CreateTgModelPanels.PARAMETERS_SETUP_PANEL && <BestPractisesButton fontSize="small" />
		);
	};

	const handleTrainingModeSelect = (mode: TrainingMode) => {
		tgModelStore.modelForm.training_mode = mode;

		if (tgModelStore.modelForm.training_mode === TrainingMode.FULLY_AUTOMATED) {
			setCurrentPanel(CreateTgModelPanels.TRAINING_VERSION_PANEL);
		} else if (tgModelStore.modelForm.training_mode === TrainingMode.EXPERT) {
			setCurrentPanel(CreateTgModelPanels.SELECT_DATASET_PANEL);
		}
	};

	const handleTrainingVersionNext = () => {
		setCurrentPanel(CreateTgModelPanels.SELECT_DATASET_PANEL);
	};

	const handleSelectDataset = (isSelectExisting: boolean = false) => {
		if (isSelectExisting) {
			if (tgModelStore.modelForm.training_mode === TrainingMode.FULLY_AUTOMATED) {
				setCurrentPanel(CreateTgModelPanels.CREATE_MODEL_DATASET_PANEL);
			} else {
				setCurrentPanel(CreateTgModelPanels.PARAMETERS_SETUP_PANEL);
			}
		} else {
			setCurrentPanel(CreateTgModelPanels.UPLOAD_METHOD_PANEL);
		}
	};

	const handleUploadMethodNext = () => {
		setCurrentPanel(CreateTgModelPanels.CREATE_MODEL_DATASET_PANEL);
	};

	const handleUseAdvancedNext = () => {
		if (tgModelStore.modelForm.training_mode === TrainingMode.EXPERT) {
			setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_PANEL);
		}
	};

	const handleCreateModelAndStartTraining = async (datasetName?: string) => {
		const completedDatasetForTraining: TgDataset = {
			...tgDatasetStore.datasetForm,
			name: datasetName ?? tgDatasetStore.datasetForm.name,
			status: "Completed",
		};
		tgModelStore.modelForm.dataset_id = tgDatasetStore.datasetForm.id;
		const [_updatedDataset, newModel] = await Promise.all([
			tgDatasetStore.updateDataset(tgDatasetStore.datasetForm.id, completedDatasetForTraining),
			tgModelStore.createModel(tgModelStore.modelForm),
		]);
		newModel.training_parameters = tgModelStore.modelForm.training_parameters;
		await tgModelStore.startTraining(newModel);
		tgDatasetStore.datasetForm = completedDatasetForTraining;
		navigate(`${RouterConstants.NEW_TG.PROJECTS.fullPath}/${projectId}/${RouterConstants.NEW_TG.MODELS.path}`);
	};

	const handleAdvancedImageUploadComplete = (firstStatus: BulkUploadStatus) => {
		setInitialStatus(firstStatus);
		setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_LOADING_PANEL);
	};

	const handleAdvancedImageUploadCancel = () => {
		tgDatasetStore.datasetForm.id = NaN;
		setInitialStatus(null);
		setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_PANEL);
	};

	const handleAdvancedImageUploadLoadingComplete = (status: BulkUploadStatus) => {
		setLastStatus(status);
		setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_COMPLETED_PANEL);
	};

	return (
		<>
			<EditorLayout
				className={
					currentPanel === CreateTgModelPanels.PARAMETERS_SETUP_PANEL
						? styles.editorContainerScroll
						: undefined
				}
				childrenClassName={`${styles.wrapper} ${
					currentPanel === CreateTgModelPanels.PARAMETERS_SETUP_PANEL ? styles.childrenOverflowVisible : ""
				}`}
				name={tgModelStore.modelForm?.name || t("title")}
				loadingSave={loadingDatasetSave}
				progress={(uploadedImages.length / tgDatasetStore.datasetForm.images.length) * 100}
				primaryButton={getPrimaryButton()}
				primaryButtonClick={primaryButtonOnClickHandler}
				secondaryButton={getSecondaryButton()}
			>
				{currentPanel === CreateTgModelPanels.TRAINING_MODE_PANEL ? (
					<TrainingModeSelection onTrainingModeSelect={handleTrainingModeSelect} />
				) : currentPanel === CreateTgModelPanels.TRAINING_VERSION_PANEL ? (
					tgModelStore.modelForm.training_mode === TrainingMode.FULLY_AUTOMATED ? (
						<TgTrainingVersionStep handleNext={handleTrainingVersionNext} />
					) : null
				) : currentPanel === CreateTgModelPanels.SELECT_DATASET_PANEL ? (
					<SelectTgDatasetStep
						handleSkip={() => handleSelectDataset(true)}
						handleSelectDataset={handleSelectDataset}
					/>
				) : currentPanel === CreateTgModelPanels.UPLOAD_METHOD_PANEL ? (
					<SelectTgUploadMethod
						handleUploadImagesNext={handleUploadMethodNext}
						handleUseAdvancedNext={handleUseAdvancedNext}
					/>
				) : currentPanel === CreateTgModelPanels.ADVANCED_UPLOAD_PANEL ? (
					<TgAdvancedImageUploadSetup onComplete={handleAdvancedImageUploadComplete} />
				) : currentPanel === CreateTgModelPanels.ADVANCED_UPLOAD_LOADING_PANEL ? (
					<AdvancedImageLoading
						datasetId={tgDatasetStore.datasetForm.id}
						onCancel={handleAdvancedImageUploadCancel}
						onCompleted={handleAdvancedImageUploadLoadingComplete}
						onError={() => setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_ERROR_PANEL)}
						initialStatus={initialStatus ?? undefined}
					/>
				) : currentPanel === CreateTgModelPanels.ADVANCED_UPLOAD_COMPLETED_PANEL ? (
					<AdvancedImageLoadingCompleted
						status={lastStatus as BulkUploadStatus}
						handleContinueClick={() => setCurrentPanel(CreateTgModelPanels.PARAMETERS_SETUP_PANEL)}
					/>
				) : currentPanel === CreateTgModelPanels.ADVANCED_UPLOAD_ERROR_PANEL ? (
					<AdvancedImageLoadingError
						onTryAgain={() => setCurrentPanel(CreateTgModelPanels.ADVANCED_UPLOAD_PANEL)}
					/>
				) : currentPanel === CreateTgModelPanels.PARAMETERS_SETUP_PANEL ? (
					<TgExpertParametersSetup
						handleCreateModelAndStartTraining={handleCreateModelAndStartTraining}
						loading={
							tgModelStore.loadingStartTraining ||
							tgModelStore.loadingCreateModel ||
							tgDatasetStore.loadingUpdateDataset
						}
						datasetSize={lastStatus?.total_processed ?? tgDatasetStore.datasetForm.images_count}
					/>
				) : currentPanel === CreateTgModelPanels.CREATE_MODEL_DATASET_PANEL ? (
					<CreateTgModelDataset />
				) : null}
			</EditorLayout>
			<CreateTgModelPopup
				open={openCreateModelPopup}
				onClose={() => setOpenCreateModelPopup(false)}
				handleCreateModelAndStartTraining={handleCreateModelAndStartTraining}
			/>
		</>
	);
};

export default observer(CreateTgModel);
